/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Handling violations", lastUpdatedDate = new Date(1713312000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Handling violations",
        description: null,
        lastUpdatedDate: "Wed Apr 17 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "depth": 1,
          "id": "violation-enforcement"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ul: "ul",
      li: "li",
      strong: "strong",
      code: "code",
      h3: "h3",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Violations occur when the events flowing through an event source do not satisfy\nthe contract applied to the source."
      }), "\n", _jsx(_components.p, {
        children: "Events can cause 3 different types of violations:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["An ", _jsx(_components.strong, {
            children: "undeclared event"
          }), " violation occurs when a specific event is not\nspecified in the contract", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["e.g. ", _jsx(_components.code, {
                children: "Button Clicked"
              }), " event is sent to Hightouch, but it is not in the\nE-commerce Events contract"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["An ", _jsx(_components.strong, {
            children: "undeclared field"
          }), " violation occurs when a field in an event is not\nspecified in the validation schema", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["e.g. ", _jsx(_components.code, {
                children: "user_name"
              }), " field is sent to Hightouch in the ", _jsx(_components.code, {
                children: "Order Completed"
              }), "\npayload, but it is not specified in the validation schema"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["A ", _jsx(_components.strong, {
            children: "schema validation"
          }), " violation occurs when a field does not pass the schema\nvalidation defined by JSON Schema rules", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["e.g. ", _jsx(_components.code, {
                children: "userId"
              }), " field is passed in as a ", _jsx(_components.code, {
                children: "string"
              }), " when the schema violation\ndefines the field as type ", _jsx(_components.code, {
                children: "number"
              })]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["All violations are written to a separate ", _jsx(_components.code, {
          children: "event_violations"
        }), " table in your\ndestination, regardless of how violation enforcement is configured."]
      }), "\n", _jsx(_components.p, {
        children: "This table has the schema:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "payload"
          }), ": JSONB"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "event_name"
          }), ": string (derived from payload)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "event_type"
          }), ": string (derived from payload)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "schema_version"
          }), ": string (derived from payload)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "has_undeclared_schema"
          }), ": boolean"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "has_undeclared_fields"
          }), ": boolean"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "has_schema_violated"
          }), ": boolean"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "violations"
          }), ": string[]"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "violation-enforcement",
        children: _jsx(_components.strong, {
          children: "Violation enforcement"
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to configure how to handle violations at both the contract\nand event level. Depending on the cleanliness of your current event tracking, you may want\nto allow violations to be written to your main event tables."
      }), "\n", _jsx(_components.p, {
        children: "This table represents the different ways Hightouch can handle a violation. The\nbolded actions represent the default behavior."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "center",
              children: "Schema validation"
            }), _jsx(_components.th, {
              align: "center",
              children: "Undeclared field"
            }), _jsx(_components.th, {
              align: "center",
              children: "Undeclared event"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "center",
              children: _jsx(_components.strong, {
                children: "Allow"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: _jsx(_components.strong, {
                children: "Allow"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: _jsx(_components.strong, {
                children: "Allow"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "center",
              children: "Block event"
            }), _jsx(_components.td, {
              align: "center",
              children: "Block event"
            }), _jsx(_components.td, {
              align: "center",
              children: "Block event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "center"
            }), _jsx(_components.td, {
              align: "center",
              children: "Omit fields"
            }), _jsx(_components.td, {
              align: "center"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Configure how to handle undeclared events at the contract level."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/contracts/violation-enforcement-contract.png",
          alt: "Violation enforcement screenshot"
        })
      }), "\n", _jsx(_components.p, {
        children: "Schema violations and undeclared fields can be configured at an event-level on the event schema page."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/contracts/violation-enforcement-event.png",
          alt: "Violation enforcement screenshot"
        })
      })]
    });
  }
}
export default MDXContent;
